import { Link } from 'react-router-dom';
import { Loader, Unplug, X, Folder } from 'lucide-react';
import type { ReactNode } from 'react';
import { getFileIcon } from '../lib/files';

// One candidate in a "what to link" list — a file whose name matched a type, or a
// whole folder whose name did. Shared by the source's Candidates tab (grouped by
// type) and the document-type page (grouped by source), so the row looks and behaves
// the same in both; the verb is always Link, only the grouping differs.
interface Props {
    kind: 'file' | 'folder';
    title: string;
    titleHref?: string;
    subtitle?: string;
    meta?: string;
    mimeType?: string | null;
    badge?: ReactNode;
    // Navaris recommends filing it, so Link takes the brand fill.
    primary: { label: string; onClick: () => void; busy?: boolean; recommended?: boolean };
    onDismiss?: () => void;
    children?: ReactNode;
}

export default function CandidateRow({
    kind, title, titleHref, subtitle, meta, mimeType, badge, primary, onDismiss, children,
}: Props) {
    const Icon = kind === 'folder' ? Folder : getFileIcon(mimeType || '');
    return (
        <div className="flex items-start gap-3 p-3 bg-offwhite border border-softgrey rounded-lg">
            <Icon className="w-5 h-5 text-brand-dark shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    {badge}
                    {titleHref ? (
                        <Link to={titleHref} className="block font-medium text-sm text-graphite truncate hover:text-brand-dark">
                            {title}
                        </Link>
                    ) : (
                        <span className="block font-medium text-sm text-graphite truncate">{title}</span>
                    )}
                </div>
                {subtitle && <span className="block text-xs text-coolslate truncate">{subtitle}</span>}
                {children}
            </div>
            {meta && (
                <span className="text-xs text-coolslate shrink-0 hidden sm:block mt-0.5">{meta}</span>
            )}
            <div className="flex items-center gap-1 shrink-0">
                <button
                    type="button"
                    disabled={primary.busy}
                    onClick={primary.onClick}
                    title={primary.label}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium border rounded-md transition-colors cursor-pointer shrink-0 disabled:opacity-50 ${primary.recommended
                        ? 'text-white bg-brand hover:bg-brand-dark border-brand'
                        : 'text-insight bg-insight/8 hover:bg-insight/15 border-insight/20'}`}
                >
                    {primary.busy ? <Loader className="w-3 h-3 animate-spin" /> : <Unplug className="w-3 h-3" />}
                    {primary.label}
                </button>
                {onDismiss && (
                    <button
                        type="button"
                        disabled={primary.busy}
                        onClick={onDismiss}
                        title="Dismiss"
                        className="inline-flex items-center px-2 py-1.5 text-xs font-medium rounded-md border border-softgrey text-coolslate hover:bg-offwhite disabled:opacity-50"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}
